import type {
  AcceptancePolicyReason,
  BlockingAcceptancePolicyReason,
  PolicyAction,
} from "@/domain/order/acceptance-policy.types"

export type AcceptancePolicyRegistryEntry = {
  title: string
  fallbackMessage: string
  defaultActions: PolicyAction[]
  /** When false, resolveAcceptancePolicyReason still maps this reason string → UNKNOWN. */
  resolveAsSelf?: boolean
}

const BACK_TO_MENU_DISMISS: PolicyAction[] = [
  { id: "back_to_menu", label: "Back to Menu" },
  { id: "dismiss", label: "Dismiss" },
]

/**
 * Central reason → presentation defaults.
 * Backend message always wins over fallbackMessage when present.
 */
export const ACCEPTANCE_POLICY_REASON_REGISTRY: Record<
  BlockingAcceptancePolicyReason,
  AcceptancePolicyRegistryEntry
> = {
  RESTAURANT_UNAVAILABLE: {
    title: "Restaurant Unavailable",
    fallbackMessage: "We're temporarily unable to accept orders.",
    defaultActions: BACK_TO_MENU_DISMISS,
    resolveAsSelf: true,
  },
  UNKNOWN_ACCEPTANCE_POLICY: {
    title: "Unable to place order",
    fallbackMessage: "Unable to place order right now.",
    defaultActions: BACK_TO_MENU_DISMISS,
    resolveAsSelf: true,
  },
  // Stubs — presentation ready; resolveAsSelf false until Phase 3+ activation.
  ACTIVE_ORDER_LIMIT_REACHED: {
    title: "Order limit reached",
    fallbackMessage: "You already have the maximum number of active orders.",
    defaultActions: [
      { id: "view_orders", label: "View My Orders" },
      { id: "dismiss", label: "Dismiss" },
    ],
    resolveAsSelf: true,
  },
  DELIVERY_AREA_UNAVAILABLE: {
    title: "This delivery location is outside our delivery area.",
    fallbackMessage: "Try another delivery address.",
    defaultActions: [
      { id: "change_address", label: "Change address" },
      { id: "dismiss", label: "Dismiss" },
    ],
    /** Legacy reason string — canonical 4095 reason is DESTINATION_NOT_SERVICEABLE. */
    resolveAsSelf: false,
  },
  DESTINATION_NOT_SERVICEABLE: {
    title: "This delivery location is outside our delivery area.",
    fallbackMessage: "Try another delivery address.",
    defaultActions: [
      { id: "change_address", label: "Change address" },
      { id: "dismiss", label: "Dismiss" },
    ],
    resolveAsSelf: true,
  },
  DESTINATION_LOCATION_REQUIRED: {
    title: "Please confirm your delivery location",
    fallbackMessage: "We need your delivery location to check whether we can deliver to you.",
    defaultActions: [{ id: "dismiss", label: "Confirm location" }],
    resolveAsSelf: true,
  },
  SERVICEABILITY_UNAVAILABLE: {
    title: "We couldn't check delivery availability right now.",
    fallbackMessage: "Please try placing your order again.",
    defaultActions: [{ id: "dismiss", label: "Try again" }],
    resolveAsSelf: true,
  },
  ITEM_NOT_AVAILABLE: {
    title: "Items no longer available",
    fallbackMessage: "Some items in your order are no longer available.",
    defaultActions: [{ id: "dismiss", label: "Review order" }],
    resolveAsSelf: true,
  },
  ORDER_VALIDATION_FAILED: {
    title: "Review your order",
    fallbackMessage: "Some items in your order need to be updated.",
    defaultActions: [{ id: "dismiss", label: "Review order" }],
    resolveAsSelf: true,
  },
  HOLIDAY_CLOSURE: {
    title: "Closed for holiday",
    fallbackMessage: "This restaurant is closed for a holiday.",
    defaultActions: BACK_TO_MENU_DISMISS,
    resolveAsSelf: false,
  },
  PLATFORM_MAINTENANCE: {
    title: "Temporarily unavailable",
    fallbackMessage: "Ordering is temporarily unavailable due to maintenance.",
    defaultActions: [{ id: "dismiss", label: "Dismiss" }],
    resolveAsSelf: false,
  },
  SCHEDULED_PAUSE: {
    title: "Ordering paused",
    fallbackMessage: "Ordering is temporarily paused.",
    defaultActions: BACK_TO_MENU_DISMISS,
    resolveAsSelf: false,
  },
  RESTAURANT_CLOSED: {
    title: "Restaurant closed",
    fallbackMessage: "This restaurant is not accepting orders right now.",
    defaultActions: BACK_TO_MENU_DISMISS,
    resolveAsSelf: true,
  },
  // Never switches fulfilment by itself: Delivery only follows an explicit choice.
  PICKUP_NOT_AVAILABLE: {
    title: "Pickup is no longer available",
    fallbackMessage:
      "This restaurant isn't taking pickup orders right now. Your order was not placed. You can switch to Delivery.",
    defaultActions: [
      { id: "switch_to_delivery", label: "Switch to Delivery" },
      { id: "dismiss", label: "Not now" },
    ],
    resolveAsSelf: true,
  },
}

export function getAcceptancePolicyRegistryEntry(
  reason: BlockingAcceptancePolicyReason
): AcceptancePolicyRegistryEntry {
  return ACCEPTANCE_POLICY_REASON_REGISTRY[reason]
}

/** Reasons the transport layer recognizes (toast suppress / policy map entry). */
export function isRegisteredAcceptanceReason(reason: string): boolean {
  if (reason === "ACTIVE_UNPAID_ORDER_EXISTS") return true
  return Object.prototype.hasOwnProperty.call(ACCEPTANCE_POLICY_REASON_REGISTRY, reason)
}

export function resolveRegisteredReasonOrUnknown(
  raw: string
): AcceptancePolicyReason {
  if (raw === "ACTIVE_UNPAID_ORDER_EXISTS") return "ACTIVE_UNPAID_ORDER_EXISTS"
  if (raw === "DELIVERY_AREA_UNAVAILABLE" || raw === "DESTINATION_NOT_SERVICEABLE") {
    return "DESTINATION_NOT_SERVICEABLE"
  }
  const entry = ACCEPTANCE_POLICY_REASON_REGISTRY[raw as BlockingAcceptancePolicyReason]
  if (!entry) return "UNKNOWN_ACCEPTANCE_POLICY"
  if (entry.resolveAsSelf) return raw as AcceptancePolicyReason
  return "UNKNOWN_ACCEPTANCE_POLICY"
}
